import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../../assets/styles/GlobalStyles';
import { FaCalendarAlt, FaFilePdf, FaArrowUp, FaTrash, FaCheckCircle, FaFileAlt } from 'react-icons/fa';
import { SelectFileButton, FileInfo, RemoveButton, ErrorMessage } from '../../requerimentos/core/components/DocumentStep/styles';
import { validatePDF } from '../../requerimentos/core/services/documentService';
import { reenviarDocumentoPendencia } from '../services/documentoReenvioService';

// Tempo que a mensagem de sucesso fica visível antes do item sumir do card
// (onReenviado dispara a remoção lá em cima, em PendenciasPage) — sem isso o
// item some na hora, antes da pessoa perceber que o reenvio deu certo.
const TEMPO_CONFIRMACAO_MS = 1500;

const ItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DocumentoIndeferido = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 8px 12px;
  background: rgba(166, 3, 14, 0.06);
  border-radius: 8px;
  font-size: 0.9rem;
  color: #1a2c3e;

  svg {
    color: ${colors.red};
    font-size: 0.9rem;
    flex-shrink: 0;
  }

  strong {
    font-weight: 600;
  }
`;

const Motivo = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #5a6e7c;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

const DataIndeferimento = styled.p`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 0.8rem;
  color: #5a6e7c;

  svg {
    opacity: 0.7;
  }
`;

const ReenvioSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const NovoArquivoLabel = styled.span`
  font-size: 0.8rem;
  color: #5a6e7c;
  width: 100%;
`;

const ReenviarButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${colors.darkBlue};
  color: ${colors.white};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${colors.lightBlue};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AvisoIndisponivel = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${colors.textMuted};
  font-style: italic;
`;

const SucessoMensagem = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #2e7d32;
`;

// Um documento indeferido dentro do card do pedido (PendenciaGrupoCard) — cada
// item cuida do próprio upload/reenvio, já que cada pendência vira uma
// chamada separada em POST /documentos/reenviar (mesmo quando várias pendências
// pertencem ao mesmo requerimento).
const PendenciaDocumentoItem = ({ pendencia, onReenviado }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [erroArquivo, setErroArquivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState(null);
  const [enviado, setEnviado] = useState(false);

  const podeReenviar = Boolean(pendencia.tiposAssunto);

  // Fallback pro raro caso em que o pedido é reconhecido mas o documento em si
  // não bate com nenhum item do documentConfig (catálogo desatualizado) — mesmo
  // assim mostra o ID bruto, nunca deixa sem nenhuma pista de qual documento é.
  const nomeDocumentoLabel = pendencia.nomeDocumento
    || (pendencia.tipoDocumentoId != null ? `Documento (tipo #${pendencia.tipoDocumentoId})` : null);

  useEffect(() => {
    if (!enviado) return undefined;
    const timer = setTimeout(() => onReenviado(pendencia.pendenciaId), TEMPO_CONFIRMACAO_MS);
    return () => clearTimeout(timer);
  }, [enviado, onReenviado, pendencia.pendenciaId]);

  const handleFileSelect = (event) => {
    const selecionado = event.target.files[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!selecionado) return;

    const { isValid, error } = validatePDF(selecionado);
    if (!isValid) {
      setErroArquivo(error);
      setFile(null);
      return;
    }

    setErroArquivo(null);
    setErroEnvio(null);
    setFile(selecionado);
  };

  const handleReenviar = async () => {
    if (!file) return;

    setEnviando(true);
    setErroEnvio(null);

    try {
      await reenviarDocumentoPendencia({ pendencia, file });
      setEnviado(true);
    } catch (error) {
      setErroEnvio(error.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <ItemContainer>
      {nomeDocumentoLabel && (
        <DocumentoIndeferido>
          <FaFileAlt />
          <span>Documento indeferido: <strong>{nomeDocumentoLabel}</strong></span>
        </DocumentoIndeferido>
      )}

      <Motivo>{pendencia.motivoIndeferimento}</Motivo>

      <DataIndeferimento>
        <FaCalendarAlt />
        Indeferido em: {pendencia.dataIndeferimento}
      </DataIndeferimento>

      {enviado ? (
        <SucessoMensagem>
          <FaCheckCircle /> Documento reenviado com sucesso!
        </SucessoMensagem>
      ) : !podeReenviar ? (
        <AvisoIndisponivel>
          Não foi possível identificar automaticamente o tipo deste pedido para reenvio. Entre em contato com a Secretaria da OAB/MS.
        </AvisoIndisponivel>
      ) : (
        <ReenvioSection>
          {nomeDocumentoLabel && (
            <NovoArquivoLabel>Novo arquivo para: <strong>{nomeDocumentoLabel}</strong></NovoArquivoLabel>
          )}
          {file ? (
            <FileInfo>
              <FaFilePdf color="#ef4444" />
              <span>{file.name}</span>
              <RemoveButton onClick={() => setFile(null)} disabled={enviando}>
                <FaTrash size={12} />
              </RemoveButton>
            </FileInfo>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                accept=".pdf,application/pdf"
              />
              <SelectFileButton onClick={() => fileInputRef.current?.click()}>
                <FaArrowUp size={12} />
                Selecionar novo PDF
              </SelectFileButton>
            </>
          )}

          {file && (
            <ReenviarButton onClick={handleReenviar} disabled={enviando}>
              {enviando ? 'Enviando...' : 'Reenviar documento'}
            </ReenviarButton>
          )}
        </ReenvioSection>
      )}

      {erroArquivo && <ErrorMessage>{erroArquivo}</ErrorMessage>}
      {erroEnvio && <ErrorMessage>{erroEnvio}</ErrorMessage>}
    </ItemContainer>
  );
};

export default PendenciaDocumentoItem;
